// WO Workflow State Engine — mirror frontend dari backend `utils/wo_workflow.py`
// (2026-06-20, user request DUAL WORKFLOW). SATU aturan canonical untuk chevron,
// status pekerjaan/administrasi, keterlambatan spesifik, dan tindakan berikutnya.
// Jangan ubah salah satu tanpa mengubah pasangannya.
//
// Chevron = posisi pekerjaan yang masih harus ditindaklanjuti SAAT INI (bukan
// histori kumulatif). Setiap WO aktif masuk TEPAT SATU chevron; Closed &
// Dibatalkan tidak masuk chevron aktif. Kategori kosong → "kategori_belum_ditentukan"
// (JANGAN menebak workflow). Milestone bertentangan → needsVerification
// ("Perlu Verifikasi"), tanpa memindahkan WO otomatis.

import { computeExpectedNet, computeTotalReceived } from "@/lib/paymentReceipts";

export const CHEVRON_UNION = [
  "wo_diterima", "site_survey", "sph_negosiasi", "pengerjaan", "selesai_fisik",
  "pembuatan_invoice", "pengiriman_invoice", "invoice_pending", "kategori_belum_ditentukan",
];

export const CHEVRON_LABEL = {
  wo_diterima: "WO Diterima",
  site_survey: "Site Survey",
  sph_negosiasi: "SPH & Negosiasi",
  pengerjaan: "Pengerjaan",
  selesai_fisik: "Selesai Fisik",
  pembuatan_invoice: "Pembuatan Invoice",
  pengiriman_invoice: "Pengiriman Invoice",
  invoice_pending: "Invoice / Menunggu Penyelesaian",
  kategori_belum_ditentukan: "Kategori WO Belum Ditentukan",
  closed: "Closed",
  cancelled_owner: "Dibatalkan",
};

const NEXT_ACTION = {
  wo_diterima: "Jadwalkan & lakukan site survey",
  site_survey: { non_urgent: "Buat & kirim SPH, lanjutkan negosiasi", urgent: "Mulai pengerjaan fisik" },
  sph_negosiasi: "Tindaklanjuti penerbitan PO",
  pengerjaan: "Selesaikan pekerjaan fisik",
  selesai_fisik: "Buat & kirim SPH",
  pembuatan_invoice: "Buat invoice",
  pengiriman_invoice: "Kirim invoice ke client",
  invoice_pending: "Tindaklanjuti pembayaran & tutup WO",
  kategori_belum_ditentukan: "Tentukan kategori WO (Urgent / Non-Urgent)",
  closed: "—",
  cancelled_owner: "—",
};

// Keterlambatan SPESIFIK per proses (jangan label umum "Terlambat").
const LATE = {
  non_urgent: {
    wo_diterima: "Terlambat Survey", site_survey: "Terlambat SPH",
    sph_negosiasi: "Terlambat PO", pengerjaan: "Terlambat Pengerjaan",
    selesai_fisik: "Terlambat Ditagihkan",
    pembuatan_invoice: "Terlambat Ditagihkan", pengiriman_invoice: "Terlambat Ditagihkan",
    invoice_pending: "Pembayaran Terlambat",
  },
  urgent: {
    wo_diterima: "Terlambat Survey", site_survey: "Terlambat Pengerjaan",
    pengerjaan: "Terlambat Pengerjaan", selesai_fisik: "Terlambat SPH",
    sph_negosiasi: "Terlambat PO",
    pembuatan_invoice: "Terlambat Ditagihkan", pengiriman_invoice: "Terlambat Ditagihkan",
    invoice_pending: "Pembayaran Terlambat",
  },
};

const ADMIN_SHORT = {
  "Persiapan SPH": "SPH Belum Dibuat",
  "Menunggu PO": "Menunggu PO",
  "Belum Ditagihkan": "Belum Ditagihkan",
  "Penagihan Berjalan": "Penagihan Berjalan",
  "Lunas": "Lunas",
  "Closed": "Closed",
};

function milestones(w) {
  const entries = Array.isArray(w.income_entries) ? w.income_entries.filter(Boolean) : [];
  const invSent = !!w.invoice_sent_date || entries.some((e) => e && e.invoice_sent_date);
  const invCreated = invSent || entries.some((e) => e && e.invoice_date && !e.is_retention_claim);
  return {
    survey: !!w.site_survey_date,
    sph: !!w.sph_submit_date || (Array.isArray(w.sph_submissions) && w.sph_submissions.length > 0),
    po: !!(w.po_number || "").trim(),
    work: !!w.work_start_date,
    done: !!w.project_done_date,
    invCreated,
    invSent,
  };
}

function adminStatus(w, m, closed) {
  if (closed) return "Closed";
  const entries = Array.isArray(w.income_entries) ? w.income_entries.filter(Boolean) : [];
  const expected = entries.reduce((s, e) => s + computeExpectedNet(e), 0);
  const received = entries.reduce((s, e) => s + computeTotalReceived(e), 0);
  if (expected > 0 && received >= expected) return "Lunas";
  if (m.invCreated) return "Penagihan Berjalan";
  if (m.po) return "Belum Ditagihkan";
  if (m.sph) return "Menunggu PO";
  return "Persiapan SPH";
}

// Tri-state KETAT (aturan user 2026-06-20): true → "urgent", false →
// "non_urgent", null/kosong/undefined/field hilang/string legacy → null
// ("Belum Diklasifikasikan"). JANGAN pakai truthiness (`!value`) — false
// adalah nilai VALID. JANGAN menebak dari milestone/status/nama/keuangan.
export function classifyWoCategory(v) {
  if (v === true) return "urgent";
  if (v === false) return "non_urgent";
  return null;
}

export const WO_CATEGORY_LABEL = {
  urgent: "Urgent Case",
  non_urgent: "Non-Urgent Case",
  unclassified: "Belum Diklasifikasikan",
};

export function woCategoryLabel(v) {
  const c = classifyWoCategory(v);
  return c ? WO_CATEGORY_LABEL[c] : WO_CATEGORY_LABEL.unclassified;
}

/** Engine canonical per WO — murni derived, tidak mengubah data. */
export function computeWoState(w, overdue = false) {
  if (!w) return null;
  const status = (w.status || "").toLowerCase();
  const category = classifyWoCategory(w.is_urgent);
  const m = milestones(w);

  if (status === "cancelled_by_owner" || status === "cancelled") {
    return { category, chevron: "cancelled_owner", chevronLabel: CHEVRON_LABEL.cancelled_owner, milestones: m, nextAction: "—", workStatus: "Dibatalkan", adminStatus: "—", displayStatus: "Dibatalkan", lateStatus: null, needsVerification: false, verificationReasons: [] };
  }
  if (status === "completed") {
    return { category, chevron: "closed", chevronLabel: "Closed", milestones: m, nextAction: "—", workStatus: "Selesai Fisik", adminStatus: "Closed", displayStatus: "Closed", lateStatus: null, needsVerification: false, verificationReasons: [] };
  }

  const verify = [];

  if (category === null) {
    const workStatus = m.done ? "Selesai Fisik" : (m.work ? "Sedang Berjalan" : "Belum Dimulai");
    const admin = adminStatus(w, m, false);
    return {
      category, chevron: "kategori_belum_ditentukan", chevronLabel: CHEVRON_LABEL.kategori_belum_ditentukan,
      milestones: m, nextAction: NEXT_ACTION.kategori_belum_ditentukan,
      workStatus, adminStatus: admin,
      displayStatus: workStatus === "Selesai Fisik" ? `Selesai Fisik · ${ADMIN_SHORT[admin]}` : workStatus,
      lateStatus: null, needsVerification: false, verificationReasons: [],
    };
  }

  let chev;
  if (category === "non_urgent") {
    if (m.invSent) chev = "invoice_pending";
    else if (m.invCreated) chev = "pengiriman_invoice";
    else if (m.done) chev = "pembuatan_invoice";
    else if (m.po) chev = "pengerjaan"; // PO terbit → Pengerjaan (belum mulai/berjalan)
    else if (m.sph) chev = "sph_negosiasi";
    else if (m.survey) chev = "site_survey";
    else chev = "wo_diterima";
    if ((m.work || m.done) && !m.po) verify.push("Pengerjaan/selesai fisik tercatat sebelum PO terbit — urutan milestone perlu diverifikasi");
    if (m.done && !m.sph) verify.push("Selesai fisik tercatat sebelum SPH — urutan milestone perlu diverifikasi");
    if (m.work && !m.sph) verify.push("Pengerjaan tercatat sebelum SPH — urutan milestone perlu diverifikasi");
  } else {
    if (m.done) {
      // Admin chain setelah selesai fisik — PO terbit langsung ke Pembuatan
      // Invoice (benar: pengerjaan & selesai fisik sudah lampau).
      if (m.invSent) chev = "invoice_pending";
      else if (m.invCreated) chev = "pengiriman_invoice";
      else if (m.po) chev = "pembuatan_invoice";
      else if (m.sph) chev = "sph_negosiasi";
      else chev = "selesai_fisik";
    } else if (m.work) {
      chev = "pengerjaan";
      if (m.sph || m.po) verify.push("SPH/PO tercatat sebelum pekerjaan selesai pada jalur urgent — urutan milestone perlu diverifikasi");
    } else if (m.survey) {
      chev = "site_survey";
      if (m.sph || m.po) verify.push("SPH/PO tercatat sebelum pengerjaan pada jalur urgent — urutan milestone perlu diverifikasi");
    } else {
      chev = "wo_diterima";
      if (m.sph || m.po || m.work) verify.push("Milestone lanjutan tercatat tanpa site survey — perlu diverifikasi");
    }
  }

  const workStatus = m.done ? "Selesai Fisik" : (m.work ? "Sedang Berjalan" : "Belum Dimulai");
  const admin = adminStatus(w, m, false);
  // WO selesai fisik TIDAK boleh "Terlambat Pekerjaan" — keterlambatan SPH/PO
  // setelah selesai fisik = keterlambatan administrasi.
  const lateStatus = overdue ? (LATE[category][chev] || null) : null;
  const displayStatus = workStatus === "Selesai Fisik" ? `Selesai Fisik · ${ADMIN_SHORT[admin]}` : workStatus;
  const na = NEXT_ACTION[chev];
  return {
    category, chevron: chev, chevronLabel: CHEVRON_LABEL[chev],
    milestones: m, nextAction: typeof na === "string" ? na : na[category],
    workStatus, adminStatus: admin, displayStatus,
    lateStatus, needsVerification: verify.length > 0, verificationReasons: verify,
  };
}

/** Ringkasan eksklusif: Σ chevron aktif + closed + dibatalkan = total WO. */
export function summarizeWoStates(states) {
  const out = {
    total: states.length, pekerjaanAktif: 0, selesaiFisikAdminTerbuka: 0, closed: 0, dibatalkan: 0,
    chevronCounts: Object.fromEntries(CHEVRON_UNION.map((k) => [k, 0])),
    categoryCounts: { urgent: 0, non_urgent: 0, belum_ditentukan: 0 },
  };
  for (const s of states) {
    if (!s) continue;
    if (s.chevron === "closed") out.closed += 1;
    else if (s.chevron === "cancelled_owner") out.dibatalkan += 1;
    else {
      out.chevronCounts[s.chevron] = (out.chevronCounts[s.chevron] || 0) + 1;
      // Keputusan user (2026-06-20): WO "Kategori Belum Ditentukan" berdiri
      // sendiri — TIDAK dihitung "Pekerjaan Aktif" maupun "Selesai Fisik".
      if (s.chevron === "kategori_belum_ditentukan") { /* berdiri sendiri */ }
      else if (s.workStatus === "Selesai Fisik") out.selesaiFisikAdminTerbuka += 1;
      else out.pekerjaanAktif += 1;
    }
    // 2026-06-20 (aturan user): Closed & Dibatalkan MEMPERTAHANKAN kategori.
    const cat = s.category || "belum_ditentukan";
    out.categoryCounts[cat] = (out.categoryCounts[cat] || 0) + 1;
  }
  return out;
}
